import Storyblok from 'storyblok-js-client';
import { mustache } from '../utility/helpers';
import { StoryblokMultilink, StoryblokMultilinkStory } from '~/.storyblok/types/storyblok';

const storyblok = new Storyblok({
  accessToken: process.env.STORYBLOK_API_KEY,
});

export type ValidTransformLinkReturn = {
  url: string;
  label?: string;
  target?: string;
  showRed: boolean;
  flag: boolean;
  isExternal: boolean;
};

export type TransformLinkReturn = ValidTransformLinkReturn | undefined;

export const transformLink = (
  link?: Partial<
    StoryblokMultilink & {
      flag: boolean;
      showRed: boolean;
      label: string;
      /**
       * @deprecated
       */
      full_slug: string;
      /**
       * @deprecated
       */
      name: string;
    }
  >,
  labelArg?: string
): TransformLinkReturn => {
  if (!link) {
    return undefined;
  }

  // prettier-ignore
  let url: string | undefined = (link.story as StoryblokMultilinkStory | undefined)?.full_slug || link.url || link.cached_url || link.full_slug;
  if (url && url.startsWith('www.')) {
    url = 'https://' + url;
  }
  let label = labelArg;
  let target = link.target;
  let showRed = link.showRed ?? false;
  let flag = link.flag ?? false;

  if (!url) {
    return undefined;
  }
  if (!url.includes('://')) {
    // avoid double slashes at URL Start
    // Remove leading slash if present
    url = url.replace(/^\//, '');
    // Add leading slash again after leading slash was removed
    url = `/${url}`;
  } else {
    target = '_blank';
  }

  if (!url.includes('://')) {
    if (!url.includes('?')) {
      if (url.slice(-1) !== '/') url = url + '/';
    } else {
      const urlSplitted = url.split('?');
      if (urlSplitted[0].slice(-1) !== '/' && !urlSplitted[0].includes('.htm'))
        url = `${urlSplitted[0]}/?${urlSplitted[1]}`;
    }
  }
  // Add trailing slashes

  if (!label) {
    label = link.label || (link.story && link.story.name) || link.name;
  }

  if (link.anchor) {
    url = `${url}#${link.anchor}`;
  }

  return {
    url,
    label,
    target,
    showRed,
    flag,
    isExternal: url.startsWith('http://') || url.startsWith('https://'),
  };
};

export const transformImage = (image: any, ratio = '3to2', caption?: string, sizes?: string) => {
  const actualRatio = ratio || '3to2';

  if (image && image.filename) {
    return {
      src: image.filename,
      alt: image.alt,
      title: image.title,
      ratio: actualRatio,
      caption: caption || image.title,
      sizes: sizes,
      focus: image.focus,
    };
  }
  return undefined;
};

export const transformVideo = (
  video: any,
  ratio = '3to2-to-16to9',
  isLoop?: boolean,
  poster?: string,
  caption?: string
) => {
  if (video && video.filename) {
    return {
      src: video.filename,
      ratio,
      isLoop,
      poster,
      caption,
    };
  }
  return undefined;
};

export const transformVimeoVideo = (vimeoID: any, ratio = '3to2-to-16to9', poster?: string, caption?: string) => {
  if (vimeoID) {
    return {
      vimeoID,
      ratio,
      poster,
      caption,
    };
  }
  return undefined;
};

export const transformTable = (tableData: any, columnHead: boolean) => {
  return {
    tableData,
    columnHead,
  };
};

export const transformDownload = (download: string, link: any, noIndex: boolean) => {
  if (download && link) {
    return {
      headline: download,
      link,
      noIndex,
    };
  }
  return undefined;
};

export const transformContactList = (contacts: Array<any>) => {
  if (contacts.length) {
    const contactMap = contacts.map(item => {
      return {
        uid: item.contact,
      };
    });
    return {
      contacts: contactMap,
    };
  }
  return undefined;
};

export const transformContactItem = (item: any) => {
  if (item.contact) {
    return {
      uid: item.contact,
    };
  }
  return undefined;
};

export const transformIframeComponent = (iframeData: any) => {
  return {
    src: iframeData.src,
    cookieConsentServiceName: iframeData.cookieConsentServiceName,
  };
};

export const unicodeReplace = (str: string) => {
  return str.replace(/["']/g, function (i: string) {
    return '&#' + i.charCodeAt(0) + ';';
  });
};

export const transformFormElement = (item: any) => {
  if (item.placeholder) {
    item.placeholder = unicodeReplace(item.placeholder);
  }
  return item;
};
export const transformFormUploadField = (item: any) => {
  item.errorMessages = {
    required: item.errorMsgRequired,
    filesize: item.errorMsgFilesize,
  };

  item.uploadOptions = {
    maxFilesize: item.maxFileSize,
    acceptedFiles: item.allowedFileTypes
      .map((type: string) => {
        switch (type) {
          case 'jpg':
            return 'image/jpeg';
          case 'png':
            return 'image/png';
          case 'xls':
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          case 'doc':
            return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword';
          case 'pdf':
            return 'application/pdf';
        }
      })
      .join(','),
    dictDefaultMessage: mustache(item.placeholder, {
      maxFilesize: item.maxFileSize,
      allowedFileTypes: item.allowedFileTypes.join(', ').toUpperCase(),
    }),
    dictFileTooBig: item.errorMsgFilesize,
  };
  return item;
};

export const transformRichText = (data: any) => {
  const resolver = storyblok.richTextResolver;
  storyblok.setComponentResolver((component: any, componentData: any) => {
    let transformedComponentData: any = {};

    switch (component) {
      case 'linkComponent':
        transformedComponentData = transformLink(componentData.link, componentData.label);
        break;
      case 'imageComponent':
        transformedComponentData = transformImage(
          componentData.image,
          componentData.imageRatio,
          componentData.imageCaption,
          'md:100vw 2xl:50vw'
        );
        break;
      case 'videoComponent':
        transformedComponentData = transformVimeoVideo(
          componentData.vimeoID,
          '3to2-to-16to9',
          componentData.poster.filename,
          componentData.caption
        );
        break;
      case 'tableComponent':
        transformedComponentData = transformTable(componentData.tableData, componentData.columnHead);
        break;
      case 'download':
        transformedComponentData = transformDownload(componentData.download, componentData.link, componentData.noIndex);
        break;
      case 'contact-list':
        component = 'ContactList';
        transformedComponentData = transformContactList(componentData.contacts);
        break;
      case 'contactItem':
        transformedComponentData = transformContactItem(componentData);
        break;
      case 'Textfield':
        transformedComponentData = transformFormElement(componentData);
        break;
      case 'TextareaComponent':
        transformedComponentData = transformFormElement(componentData);
        break;
      case 'Uploadfield':
        transformedComponentData = transformFormUploadField(componentData);
        break;
      // case 'iframeComponent':
      // 	transformedComponentData = transformIframeComponent(componentData)
      // 	break
      default:
        transformedComponentData = componentData;
    }
    // prettier-ignore
    return `<component v-bind='${JSON.stringify(transformedComponentData)}' :is="'${component}'"/>`
  });

  return `<div>` + resolver.render(data) + '</div>';
};

export const transformDate = (dateValue: any) => {
  if (dateValue) {
    dateValue = dateValue
      //.match(/([^T]+)/)[0]
      .replace('00:00', '')
      .replace('12:00', '')
      .replace(/\s/g, '')
      .split('-')
      .reverse()
      .join('.');
    return {
      dateValue: dateValue,
    };
  } else {
    const date = new Date();
    let day = String(date.getDate()).padStart(2, '0');
    let month = String(date.getMonth() + 1).padStart(2, '0');
    let year = date.getFullYear();
    dateValue = `${day}.${month}.${year}`;
    return {
      dateValue: dateValue,
    };
  }
};
